// Memo-Werkzeuge (Phase E): Annahmen als Abfrage, Texte und Diagrammhilfen für Berichtsseite und Auswertung.
import type { DbPortalEntgeltMonat, DbPortalEntgeltQuelle, DbPortalFristStatus, DbPortalKenntnis, DbPortalReifeStufe, DbPortalVerstoss } from '@gql/shared-types';

export type Zahlungsrhythmus = 'monat' | 'quartal';

export interface Annahmen {
  stichtag: string;
  kenntnis: DbPortalKenntnis;
  kenntnisDatum: string;
  verstoss: DbPortalVerstoss;
  beendigung: string;
  /** Zahlungszeitpunkt: Monatserster oder Quartalserster (Vorauszahlung je Quartal) */
  zahlung: Zahlungsrhythmus;
  /** Hemmungszeitraum nach § 209 BGB – wirkt nur, wenn beide Daten gesetzt sind */
  hemmungVon: string;
  hemmungBis: string;
  /** bis zu drei Vergleichswerte in Euro (Eingabe), leer = keine Schätzung */
  vergleichswerte: [string, string, string];
}

export const heuteIso = (): string => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });

export const standardAnnahmen = (): Annahmen => ({
  stichtag: heuteIso(),
  kenntnis: 'zahlung',
  kenntnisDatum: '',
  verstoss: 'einzel',
  beendigung: '',
  zahlung: 'monat',
  hemmungVon: '',
  hemmungBis: '',
  vergleichswerte: ['', '', ''],
});

const BETRAG = /^\d{1,6}(\.\d{1,2})?$/;

/** Abfrage-String für /analyse, /bericht.md und /admin/auswertung; nur Abweichungen vom Standard, Beträge mit Punkt. */
export function annahmenAbfrage(a: Annahmen): string {
  const q = new URLSearchParams();
  if (a.stichtag) q.set('stichtag', a.stichtag);
  if (a.kenntnis !== 'zahlung') q.set('kenntnis', a.kenntnis);
  if (a.kenntnis === 'datum' && a.kenntnisDatum) q.set('kenntnisDatum', a.kenntnisDatum);
  if (a.verstoss !== 'einzel') q.set('verstoss', a.verstoss);
  if (a.verstoss === 'dauer' && a.beendigung) q.set('beendigung', a.beendigung);
  if (a.zahlung !== 'monat') q.set('zahlung', a.zahlung);
  if (a.hemmungVon && a.hemmungBis && a.hemmungBis > a.hemmungVon) {
    q.set('hemmungVon', a.hemmungVon);
    q.set('hemmungBis', a.hemmungBis);
  }
  const werte = [...new Set(a.vergleichswerte.map((w) => w.trim().replace(',', '.')).filter((w) => BETRAG.test(w)))].slice(0, 3);
  if (werte.length) q.set('vergleichswert', werte.join(';'));
  return q.toString();
}

export const REIFE_TEXT: Record<DbPortalReifeStufe, string> = {
  bereit: 'bereit für die Kurz-Einschätzung',
  teilweise: 'teilweise – Lücken schließen',
  unzureichend: 'unzureichend',
};
export const QUELLE_TEXT: Record<DbPortalEntgeltQuelle, string> = {
  komponenten: 'Tabellensumme',
  entgeltaenderung: 'Entgeltänderung',
  entgelt: 'Entgelt laut Unterlage',
  hergeleitet: 'hergeleitet',
};
export const KENNTNIS_TEXT: Record<DbPortalKenntnis, string> = {
  zahlung: 'mit der Zahlung (ungünstigste Annahme)',
  datum: 'zu einem festen Datum',
  unbekannt: 'unbekannt (nur Höchstfristen)',
};
export const VERSTOSS_TEXT: Record<DbPortalVerstoss, string> = {
  einzel: 'je Zahlung beendet (Einzelverstoß)',
  dauer: 'Dauerverstoß bis zur Beendigung',
};
export const ZAHLUNG_TEXT: Record<Zahlungsrhythmus, string> = {
  monat: 'zum Monatsersten',
  quartal: 'zum Quartalsersten (Vorauszahlung je Quartal)',
};
export const FRIST_TEXT: Record<DbPortalFristStatus, string> = { laeuft: 'läuft', offen: 'noch nicht angelaufen', verjaehrt: 'verjährt' };

export const monatText = (m: string | null): string => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : '–');

export interface DiagrammAbschnitt {
  sicher: boolean;
  /** [Monatsindex, Cent] */
  punkte: Array<[number, number]>;
}

/** Zusammenhängende Abschnitte belegter Monate, getrennt nach „sicher“ – jeder Abschnitt wird eine Linie. */
export function diagrammAbschnitte(monate: DbPortalEntgeltMonat[]): DiagrammAbschnitt[] {
  const aus: DiagrammAbschnitt[] = [];
  let aktuell: DiagrammAbschnitt | null = null;
  monate.forEach((m, i) => {
    if (m.cent === null) {
      aktuell = null;
      return;
    }
    if (!aktuell || aktuell.sicher !== m.sicher) {
      // Übergang ohne Lücke: letzten Punkt wiederholen, damit die Linie durchläuft
      const vorher = aktuell?.punkte.at(-1);
      aktuell = { sicher: m.sicher, punkte: vorher ? [vorher] : [] };
      aus.push(aktuell);
    }
    aktuell.punkte.push([i, m.cent]);
  });
  return aus;
}

/** SVG-Pfad („M x y L x y …“) für einen Abschnitt; x je Monat, y linear mit 0 unten. */
export function diagrammPfad(a: DiagrammAbschnitt, x: (i: number) => number, y: (cent: number) => number): string {
  return a.punkte.map(([i, c], n) => `${n ? 'L' : 'M'}${x(i).toFixed(1)} ${y(c).toFixed(1)}`).join(' ');
}

/** Achsenobergrenze: nächster „runder“ Eurobetrag über dem Höchstwert (mindestens 50 €). */
export function achsenMaximum(maxCent: number): number {
  const euro = Math.max(50, (maxCent / 100) * 1.05);
  const stufe = 10 ** Math.floor(Math.log10(euro));
  const faktor = [1, 2, 2.5, 5, 10].find((f) => f * stufe >= euro) ?? 10;
  return Math.round(faktor * stufe * 100);
}
